import { createContext, useContext, useMemo, useState } from 'react';
import type { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';

type IdentityAccountState = {
  initialized: boolean;
  entityResourceList: CoinbaseProfileMenuResource[] | undefined;
};

type IdentityAccountStateContextValue = IdentityAccountState & {
  setInitialized: (initialized: boolean) => void;
  setEntityResourceList: (resources: CoinbaseProfileMenuResource[] | undefined) => void;
};

const IdentityAccountStateContext = createContext<IdentityAccountStateContextValue>({
  initialized: false,
  entityResourceList: undefined,
  setInitialized: () => {},
  setEntityResourceList: () => {},
});

type IdentityAccountStateProviderProps = { children: React.ReactNode };

export function IdentityAccountStateProvider({ children }: IdentityAccountStateProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const [entityResourceList, setEntityResourceList] = useState<
    CoinbaseProfileMenuResource[] | undefined
  >(undefined);

  const value = useMemo(
    () => ({
      initialized,
      entityResourceList,

      setInitialized,
      setEntityResourceList,
    }),
    [initialized, entityResourceList],
  );

  return (
    <IdentityAccountStateContext.Provider value={value}>
      {children}
    </IdentityAccountStateContext.Provider>
  );
}

/**
 * Internal hook that returns all state values AND setters.
 * Use this when you need to modify the state (e.g., inside components that manage the state).
 */
export function useIdentityAccountStateInternal() {
  const context = useContext(IdentityAccountStateContext);
  return context;
}

/**
 * Public hook that returns only the read-only state values.
 * Use this in components that only need to read the state.
 */
export function useIdentityAccountState(): IdentityAccountState {
  const context = useContext(IdentityAccountStateContext);

  return {
    initialized: context.initialized,
    entityResourceList: context.entityResourceList,
  };
}
